import { computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import { PRIORITY_CONDITION_VALUES } from 'dashboard/constants/automation';

/**
 * Composable for handling macro-related functionality
 * @returns {Object} An object containing the getMacroDropdownValues function
 */
export const useMacros = () => {
  const { t } = useI18n();
  const getters = useStoreGetters();

  const labels = computed(() => getters['labels/getLabels'].value);
  const teams = computed(() => getters['teams/getTeams'].value);
  const agents = computed(() => getters['agents/getVerifiedAgents'].value);
  const agentBots = computed(() => getters['agentBots/getBots'].value);
  const contactReasons = computed(
    () => getters['contactReasons/getContactReasons'].value
  );

  /**
   * Get dropdown values based on the specified type
   * @param {string} type - The type of dropdown values to retrieve
   * @returns {Array} An array of dropdown values
   */
  const getMacroDropdownValues = type => {
    switch (type) {
      case 'notify_on_slack':
        return agents.value;
      case 'assign_team':
      case 'send_email_to_team':
        return teams.value;
      case 'assign_agent':
        return [{ id: 'self', name: 'Self' }, ...agents.value];
      case 'assign_agent_bot':
        return agentBots.value;
      case 'add_label':
      case 'remove_label':
        return labels.value.map(i => ({
          id: i.title,
          name: i.title,
        }));
      case 'remove_contact_reasons':
      case 'add_contact_reasons': {
        const mapReason = reason => ({
          id: reason.id,
          name: reason.name,
          additionalInfo: {
            children: (reason.child_reasons ?? []).map(mapReason),
            subIds: reason.sub_ids,
          },
          icon: h('span', {
            class: 'i-lucide-tag',
            style: {
              color: reason.config?.color,
              height: '12px',
              width: '12px',
            },
          }),
        });
        return contactReasons.value.map(mapReason);
      }
      case 'change_priority':
        return PRIORITY_CONDITION_VALUES.map(item => ({
          id: item.id,
          name: t(`MACROS.PRIORITY_TYPES.${item.i18nKey}`),
        }));
      default:
        return [];
    }
  };

  return {
    getMacroDropdownValues,
  };
};
